import { useState } from 'react'
import { PhoneOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useProfile } from '@/api/profile'
import {
  useCompanyCallLogs,
  type CompanyCallLogsFilters,
} from '@/api/callLogs'
import { HistoryFilters } from './history/HistoryFilters'
import { CallLogsTable } from './history/CallLogsTable'
import { CallLogsMobile } from './history/CallLogsMobile'
import { Pagination } from './history/Pagination'

const DEFAULT_FILTERS: CompanyCallLogsFilters = {
  period: 'all',
  status: 'all',
  direction: 'all',
  page: 1,
  pageSize: 25,
}

export function HistoryView() {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const companyId = profile?.company

  const [filters, setFilters] = useState<CompanyCallLogsFilters>(DEFAULT_FILTERS)
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null)
  const { data, isLoading, isFetching } = useCompanyCallLogs(companyId, filters)

  const logs = data?.results ?? []
  const total = data?.total ?? 0
  const totalPages = data?.total_pages ?? 1
  const pageSize = filters.pageSize ?? 25
  const page = filters.page ?? 1

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-6 space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
              {t('manager.history.sectionTitle')}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('manager.history.sectionSubtitle')}
            </p>
          </div>

          {/* Filtres */}
          <HistoryFilters filters={filters} onChange={setFilters} />

          {/* Compteur */}
          {!isLoading && (
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                {t('manager.history.callsFound', { count: total })}
              </span>
              {isFetching && (
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {t('manager.history.refreshing')}
                </span>
              )}
            </div>
          )}

          {/* État : loading / empty / table */}
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-12 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse"
                  style={{ animationDelay: `${i * 80}ms` }}
                />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16">
              <PhoneOff className="h-12 w-12 text-slate-200 dark:text-slate-700 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                {t('manager.history.emptyTitle')}
              </h3>
              <p className="text-sm text-slate-400 dark:text-slate-500 max-w-sm mx-auto">
                {t('manager.history.emptyDescription')}
              </p>
            </div>
          ) : companyId ? (
            <>
              <CallLogsTable
                logs={logs}
                companyId={companyId}
                expandedLogId={expandedLogId}
                onToggleExpand={setExpandedLogId}
              />
              <CallLogsMobile
                logs={logs}
                companyId={companyId}
                expandedLogId={expandedLogId}
                onToggleExpand={setExpandedLogId}
              />
              <Pagination
                page={page}
                totalPages={totalPages}
                pageSize={pageSize}
                total={total}
                onPageChange={(p) => setFilters({ ...filters, page: p })}
                onPageSizeChange={(s) =>
                  setFilters({ ...filters, pageSize: s, page: 1 })
                }
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}
